import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { getUiLang } from '../utils/lang';
import axios from 'axios';
import SEO from '../components/SEO';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Brands = () => {
  const { t, i18n } = useTranslation();
  const [brands, setBrands] = useState([]);
  const [loading, setLoading] = useState(true);

  // SEO meta data by language
  const seoData = {
    sk: {
      title: 'Značky',
      description: 'Naše značky slnečných ochranných fólií: Armolan, Madico, Solar Gard, Bruxsafol, LLumar. Kvalitné produkty pre váš dom a kanceláriu.',
      keywords: 'Armolan, Madico, Solar Gard, Bruxsafol, LLumar, značky fólií'
    },
    cs: {
      title: 'Značky',
      description: 'Naše značky slunečních ochranných fólií: Armolan, Madico, Solar Gard, Bruxsafol, LLumar.',
      keywords: 'Armolan, Madico, Solar Gard, Bruxsafol, LLumar, značky fólií'
    },
    en: {
      title: 'Brands',
      description: 'Our window film brands: Armolan, Madico, Solar Gard, Bruxsafol, LLumar. Quality products for your home and office.',
      keywords: 'Armolan, Madico, Solar Gard, Bruxsafol, LLumar, film brands'
    },
    de: {
      title: 'Marken',
      description: 'Unsere Sonnenschutzfolien-Marken: Armolan, Madico, Solar Gard, Bruxsafol, LLumar.',
      keywords: 'Armolan, Madico, Solar Gard, Bruxsafol, LLumar, Folien Marken'
    },
    ru: {
      title: 'Бренды',
      description: 'Наши бренды солнцезащитных пленок: Armolan, Madico, Solar Gard, Bruxsafol, LLumar.',
      keywords: 'Armolan, Madico, Solar Gard, Bruxsafol, LLumar, бренды пленок'
    }
  };

  const currentSeo = seoData[i18n.language] || seoData.sk;

  useEffect(() => {
    const fetchBrands = async () => {
      try {
        const response = await axios.get(`${API}/brands`);
        setBrands(response.data);
      } catch (error) {
        console.error('Error fetching brands:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchBrands();
  }, []);

  const getTranslation = (obj, field) => {
    return obj?.translations?.[getUiLang(i18n)]?.[field] || obj?.translations?.['sk']?.[field] || obj?.translations?.['en']?.[field] || '';
  };

  // Brand logos/colors
  const brandStyles = {
    'Armolan': { color: '#1E3A8A', logo: '🛡️' },
    'Madico': { color: '#059669', logo: '🌿' },
    'Solar Gard': { color: '#F59E0B', logo: '☀️' },
    'Bruxsafol': { color: '#7C3AED', logo: '🔮' },
    'LLumar': { color: '#DC2626', logo: '🔥' }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <SEO 
        title={currentSeo.title}
        description={currentSeo.description}
        keywords={currentSeo.keywords}
        url="/brands"
      />
      {/* Hero Section */}
      <div className="bg-gradient-to-r from-orange-500 to-yellow-400 text-white py-16">
        <div className="container mx-auto px-4 text-center">
          <h1 className="text-4xl md:text-5xl font-bold mb-4" data-testid="brands-title">
            {t('brands.title')}
          </h1>
          <p className="text-xl opacity-90 max-w-2xl mx-auto">
            {t('brands.subtitle')}
          </p>
        </div>
      </div>

      {/* Brands Grid */}
      <div className="container mx-auto px-4 py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {brands.map((brand) => {
            const style = brandStyles[brand.name] || { color: '#F97316', logo: '🏷️' };
            return (
              <div
                key={brand.id}
                className="bg-white rounded-2xl shadow-lg overflow-hidden hover:shadow-xl transition-shadow duration-300"
                data-testid={`brand-card-${brand.id}`}
              >
                {/* Brand Header */}
                <div 
                  className="h-32 flex items-center justify-center"
                  style={{ backgroundColor: style.color }}
                >
                  <span className="text-6xl">{style.logo}</span>
                </div>
                
                {/* Brand Content */}
                <div className="p-6">
                  <h2 className="text-2xl font-bold mb-3" style={{ color: style.color }}>
                    {brand.name}
                  </h2>
                  <p className="text-gray-600 mb-4 line-clamp-3">
                    {getTranslation(brand, 'description') || t('brands.subtitle')}
                  </p>
                  
                  {/* View Products Button */}
                  <Link
                    to={`/catalog?brands=${brand.id}`}
                    className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-white transition-colors"
                    style={{ backgroundColor: style.color }}
                    data-testid={`brand-link-${brand.id}`}
                  >
                    {t('categories.view_all')}
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </Link>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Why Choose Our Brands */}
      <div className="bg-white py-16">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl font-bold text-center mb-12">{t('services.why_us')}</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            <div className="text-center">
              <div className="w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-orange-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <h3 className="font-semibold mb-2">{t('services.value1_title')}</h3>
              <p className="text-gray-600 text-sm">{t('services.value1_desc')}</p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-orange-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <h3 className="font-semibold mb-2">{t('services.value2_title')}</h3>
              <p className="text-gray-600 text-sm">{t('services.value2_desc')}</p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-orange-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                </svg>
              </div>
              <h3 className="font-semibold mb-2">{t('services.value3_title')}</h3>
              <p className="text-gray-600 text-sm">{t('services.value3_desc')}</p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-orange-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
                </svg>
              </div>
              <h3 className="font-semibold mb-2">{t('services.value4_title')}</h3>
              <p className="text-gray-600 text-sm">{t('services.value4_desc')}</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Brands;
